import { tKey } from 'ember-intl';
import { PA_AUTOPILOT_SCHEDULE_OPTIONS } from 'frontend-utils/models/hotel';
import { ROOMTYPES } from 'frontend-utils/models/roomtype';
import { CENTRAL_MAPPING_FIXED_CATEGORIES } from 'lumen/utils/central-mapping-constants';
import { MEALTYPES } from 'lumen/utils/constants/general-constants';

export const MAX_ALLOWED_MONTHS_IN_FUTURE = 12;

// Currently only reference period of 1 day (yesterday) is supported
export const PA_REF_DAYS_AGO = 1;

// Current max value for most PRISM Number inputs used in Pricing Assistant
// If users would write very large numbers (e.g. 20 0's), JavaScript will switch to scientific notation and we want to avoid that.
// Currently went for this large number, as it seems like a very reasonable limit/safety net for prices that should be entered in the product.
// If it's ever not enough in certain currencies, we can switch this to Number.MAX_SAFE_INTEGER as well.
export const NUMBER_INPUT_MAX_VALUE = 99999999;

export const STAY_RESTRICTIONS_RATE_GROUP_IDS = {
  DEEP_DISCOUNT: 'deep_discount',
} as const;

export type StayRestrictionsRateGroupIdsType =
  (typeof STAY_RESTRICTIONS_RATE_GROUP_IDS)[keyof typeof STAY_RESTRICTIONS_RATE_GROUP_IDS];

// These are the possible action values for the recommendations (Note: these are not recommendationActions. That's a separate object 😅)
export const RECOMMENDATION_ACTION_STATES = {
  ACCEPTED: 'accepted', // Recommendation accepted by the user, pushed if not undone in the undo window time
  SNOOZED: 'snoozed', // Recommendation will not be shown for 24h
  LONGSNOOZED: 'long_snoozed', // Recommendation will not be shown for 7d
  PUSHING: 'pushing', // Recommendation is being pushed to the PMS, but not yet acknowledged
  PUSHED: 'pushed', // Recommendation was pushed to PMS
  EXCEPTION: 'exception', // Recommendation was rejected by PMS
};

// Possible action values that can be passed when creating a recommendationAction (e.g. accepting or snoozing a recommendation)
export const RECOMMENDATIONACTIONS_ACTIONS = {
  ACCEPTED: 'accepted',
  SNOOZED: 'snoozed',
  LONGSNOOZED: 'long_snoozed',
} as const;

export const RECOMMENDATION_RATE_DIFFERENCE_FORMATS = {
  ABSOLUTE: 'absolute',
  RELATIVE: 'relative',
};

// Constants that can be used in the request when creating a recommendation action
// This allows BE to know what kind of price is being actioned (e.g. PMS price requires no further offset handling, while OTA prices do)
// Default behavior should be the OTA price one
export const RECOMMENDATION_ACTION_PRICE_TYPES = {
  PMS_PRICE: 'pms_price',
  OTA_PRICE: 'ota_price',
} as const;

export type RecommendationActionPriceTypesType =
  (typeof RECOMMENDATION_ACTION_PRICE_TYPES)[keyof typeof RECOMMENDATION_ACTION_PRICE_TYPES];

/* Similar constants as RATE_PARAM in general-constants */
/* Used our own for CA because we need something similar, but not 100% the same (currently no rateParam yet) */
export const RATE_TYPES = {
  BEST_FLEX: 'best_flex',
  LOWEST: 'lowest',
};

export const RATE_TYPE_TO_TRANSLATION_KEYS = {
  [RATE_TYPES.BEST_FLEX]: 'rates.bar_short',
  [RATE_TYPES.LOWEST]: 'rates.lowest',
};

export const RATE_TYPES_ICONS = {
  [RATE_TYPES.BEST_FLEX]: 'rates-best-flex',
  [RATE_TYPES.LOWEST]: 'rates-lowest',
} as const;

// source returned by API on price push
// Indicates whether the user pushed a price for a day with recommendation or without recommendation. And if for a day with recommendation, whether pushed price was recommended by us or modified by user
export const RECOMMENDATION_SOURCES = {
  SYSTEM_REC: 'system_rec',
  SYSTEM_MODIFIED_REC: 'system_rec_mod',
  USER_REC: 'user_rec',
};

// A recommendation object's pushResultMessage property values in case its price push failed.
// More info about these error codes: https://otainsight.atlassian.net/wiki/spaces/EN/pages/3399483393/Commercial+Assistant+Exceptions#Adding-new-codes
export const PUSH_ERROR_CODES = {
  CONFIGURATION_ISSUE: 'config_issue',
  RETRYABLE_ISSUE: 'retryable_issue',
  GENERIC_ISSUE: 'generic_issue',
  MAINTENANCE_WINDOW: 'maintenance_window',
  PARTIAL_PUSH_ISSUE: 'partial_push_issue',
};

export const PUSH_ERROR_CODES_TRANSLATION_KEY_MAPPING = {
  [PUSH_ERROR_CODES.CONFIGURATION_ISSUE]: tKey(
    'pricing_assistant.push_error_code.config_issue',
  ),
  [PUSH_ERROR_CODES.RETRYABLE_ISSUE]: tKey(
    'pricing_assistant.push_error_code.retryable_issue',
  ),
  [PUSH_ERROR_CODES.GENERIC_ISSUE]: tKey(
    'pricing_assistant.push_error_code.generic_issue',
  ),
  [PUSH_ERROR_CODES.MAINTENANCE_WINDOW]: tKey(
    'pricing_assistant.push_error_code.maintenance_window',
  ),
  [PUSH_ERROR_CODES.PARTIAL_PUSH_ISSUE]: tKey(
    'pricing_assistant.push_error_code.partial_push_issue',
  ),
};

export const PRICE_DIFFERENCE_TYPES = {
  ABSOLUTE: 'absolute',
  RELATIVE: 'relative',
};

// Note: all constants below are used in the CA PMS settings page (closely tied to CA, but a separate page)
export const PMS_SETTINGS_OFFSET_TYPES = {
  ABSOLUTE: 'absolute',
  RELATIVE: 'relative',
};

// Where the channel difference offset is managed. When sourced from the channel
// manager, the offset is read-only here and editable in Channel Manager instead.
export const PMS_SETTINGS_OFFSET_SOURCES = {
  CHANNEL_MANAGER: 'channel_manager',
  PMS_SETTINGS: 'pms_settings',
};

// Array of mealtypes that are supported by PMSs. Subset of MEALTYPES used in RateI (e.g. no ANY/WALK_IN_BREAKFAST/INCLUSIVE_BREAKFAST,...)
export const SUPPORTED_MEALTYPES = [
  MEALTYPES.ROOM_ONLY,
  MEALTYPES.BED_AND_BREAKFAST,
  MEALTYPES.HALF_BOARD,
  MEALTYPES.FULL_BOARD,
  MEALTYPES.ALL_IN,
];

export const OCCUPANCY_GUESTS = {
  ADULT_1: 'adult_1',
  ADULT_2: 'adult_2',
  EXTRA_ADULT: 'extra_adult',
};

// Constant to distinguish "Edit my price" button so that recommendationActionButtonTooltip component can show correct tooltips
export const EDIT_RECOMMENDED_PRICE_BUTTON_TYPE = 'edit' as const;

export const PA_AUTOPILOT_SCHEDULE_OPTIONS_ORDER = {
  [PA_AUTOPILOT_SCHEDULE_OPTIONS.DAILY]: 3,
  [PA_AUTOPILOT_SCHEDULE_OPTIONS.HOURLY]: 1,
  [PA_AUTOPILOT_SCHEDULE_OPTIONS.THREE_TIMES_DAILY]: 2,
};

export const PA_AUTOPILOT_SCHEDULE_OPTIONS_TRANSLATION_KEY_MAPPING = {
  [PA_AUTOPILOT_SCHEDULE_OPTIONS.DAILY]: tKey(
    'pricing_assistant.autopilot.settings.label.frequency.daily',
  ),
  [PA_AUTOPILOT_SCHEDULE_OPTIONS.HOURLY]: tKey(
    'pricing_assistant.autopilot.settings.label.frequency.hourly',
  ),
  [PA_AUTOPILOT_SCHEDULE_OPTIONS.THREE_TIMES_DAILY]: tKey(
    'pricing_assistant.autopilot.settings.label.frequency.three_times_daily',
  ),
};

export const PA_AUTOPILOT_LEADTIME_MAX_VALUE = 365;

export const AUTOPILOT_DAY_ACTIONS = {
  ENABLE: 'enable',
  DISABLE: 'disable',
};

// Supported PMS rate refresh types
export const PA_PMS_RATE_REFRESH_TYPE = {
  REALTIME: 'realtime',
  SCHEDULED: 'scheduled',
  NO_REFRESH: 'no_refresh',
  REFRESHABLE: 'refreshable',
};

// The minimum amount of days the user needs to wait before they can edit their preferred rate again
export const PREFERRED_RATE_MIN_DAYS_BETWEEN_EDITING = 7;

export const RECOMMENDATION_REASON_TYPE_COMPSET = {
  FALLBACK: 'compset.fallback',
  SOLDOUT: 'compset.soldout',
  VS_NORMAL: 'compset.vs_normal',
  VS_YESTERDAY: 'compset.vs_yesterday',
} as const;

export type RecommendationReasonTypeCompset =
  (typeof RECOMMENDATION_REASON_TYPE_COMPSET)[keyof typeof RECOMMENDATION_REASON_TYPE_COMPSET];

export const RECOMMENDATION_REASON_TYPE_OCCUPANCY = {
  PREDICTED: 'occupancy.predicted',
  VS_EXPECTED: 'occupancy.vs_expected',
  VS_YESTERDAY: 'occupancy.vs_yesterday',
} as const;

export type RecommendationReasonTypeOccupancy =
  (typeof RECOMMENDATION_REASON_TYPE_OCCUPANCY)[keyof typeof RECOMMENDATION_REASON_TYPE_OCCUPANCY];

export const RECOMMENDATION_REASON_TYPE_USER_RULE = {
  MIN_MAX: 'user_rules.min_max',
  PRICE_SETTINGS: 'user_rules.pricing_settings',
  ROUNDING: 'user_rules.rounding',
} as const;

export type RecommendationReasonTypeUserRule =
  (typeof RECOMMENDATION_REASON_TYPE_USER_RULE)[keyof typeof RECOMMENDATION_REASON_TYPE_USER_RULE];

// BE returns us translation keys for each recommendation reason and its detail. We map those keys to FE lokalise translation keys
export const RECOMMENDATION_REASON_TRANSLATION_KEY = {
  'pricing_assistant.reason.occupancy.vs_yesterday.high': tKey(
    'pricing_assistant.reason.occupancy.vs_yesterday.high',
  ),
  'pricing_assistant.reason.occupancy.vs_yesterday.detail.high': tKey(
    'pricing_assistant.reason.occupancy.vs_yesterday.detail.high',
  ),
  'pricing_assistant.reason.occupancy.vs_expected.high': tKey(
    'pricing_assistant.reason.occupancy.vs_expected.high',
  ),
  'pricing_assistant.reason.occupancy.vs_expected.detail.high': tKey(
    'pricing_assistant.reason.occupancy.vs_expected.detail.high',
  ),
  'pricing_assistant.reason.occupancy.vs_expected.low': tKey(
    'pricing_assistant.reason.occupancy.vs_expected.low',
  ),
  'pricing_assistant.reason.occupancy.vs_expected.detail.low': tKey(
    'pricing_assistant.reason.occupancy.vs_expected.detail.low',
  ),
  'pricing_assistant.reason.occupancy.predicted.high': tKey(
    'pricing_assistant.reason.occupancy.predicted.high',
  ),
  'pricing_assistant.reason.occupancy.predicted.detail.high': tKey(
    'pricing_assistant.reason.occupancy.predicted.detail',
  ),
  'pricing_assistant.reason.occupancy.predicted.low': tKey(
    'pricing_assistant.reason.occupancy.predicted.low',
  ),
  'pricing_assistant.reason.occupancy.predicted.detail.low': tKey(
    'pricing_assistant.reason.occupancy.predicted.detail',
  ),
  'pricing_assistant.reason.user_rules.pricing_settings': tKey(
    'pricing_assistant.reason.user_rules.pricing_settings',
  ),
  'pricing_assistant.reason.user_rules.rounding.detail.high': tKey(
    'pricing_assistant.reason.user_rules.rounding.detail',
  ),
  'pricing_assistant.reason.user_rules.rounding.detail.low': tKey(
    'pricing_assistant.reason.user_rules.rounding.detail',
  ),
  'pricing_assistant.reason.user_rules.min_max.detail.high': tKey(
    'pricing_assistant.reason.user_rules.min_max.detail.high',
  ),
  'pricing_assistant.reason.user_rules.min_max.detail.low': tKey(
    'pricing_assistant.reason.user_rules.min_max.detail.low',
  ),
  'pricing_assistant.reason.compset.soldout.high': tKey(
    'pricing_assistant.reason.compset.soldout.high',
  ),
  'pricing_assistant.reason.compset.vs_normal.high': tKey(
    'pricing_assistant.reason.compset.vs_normal.high',
  ),
  'pricing_assistant.reason.compset.vs_normal.low': tKey(
    'pricing_assistant.reason.compset.vs_normal.low',
  ),
  'pricing_assistant.reason.compset.vs_yesterday.high': tKey(
    'pricing_assistant.reason.compset.vs_yesterday.high',
  ),
  'pricing_assistant.reason.compset.vs_yesterday.low': tKey(
    'pricing_assistant.reason.compset.vs_yesterday.low',
  ),
  'pricing_assistant.reason.compset.fallback.low': tKey(
    'pricing_assistant.reason.compset.fallback',
  ),
  'pricing_assistant.reason.compset.fallback.high': tKey(
    'pricing_assistant.reason.compset.fallback',
  ),
  'pricing_assistant.reason.compset.fallback.detail.low': tKey(
    'pricing_assistant.reason.compset.fallback.detail',
  ),
  'pricing_assistant.reason.compset.fallback.detail.high': tKey(
    'pricing_assistant.reason.compset.fallback.detail',
  ),
};

export type RecommendationReasonTranslationKey =
  keyof typeof RECOMMENDATION_REASON_TRANSLATION_KEY;

export const PMS_STATUSES = {
  INTEGRABLE: 'integrable',
  NON_INTEGRABLE: 'non-integrable',
  BETA: 'beta',
};

export const PREFERRED_CHANNEL_STATUSES = {
  AVAILABLE: 'available',
  SUPPORTED: 'supported',
  UNSUPPORTED: 'unsupported',
  IN_PROGRESS: 'in_progress',
};

export const DETAIL_TAB_PRICING_TABS = {
  CALCULATION: 'calculation',
  ROOMS: 'rooms',
};

export const PRICE_RANGE_STRATEGY_CONDITION_TYPES = {
  OTB: 'otb',
  OTB_FORECAST: 'otb_forecast',
  LEADTIME: 'leadtime',
};

export const PRICE_RANGE_STRATEGY_CONDITION_OPERATORS = {
  GT: 'GT',
  LT: 'LT',
  GTE: 'GTE',
  LTE: 'LTE',
};

export const PRICE_RANGE_STRATEGY_CONDITION_TRANSLATIONS = {
  [PRICE_RANGE_STRATEGY_CONDITION_OPERATORS.GT]: tKey(
    'pricing_assistant.recommendations.price_range.formatted_condition.gt',
  ),
  [PRICE_RANGE_STRATEGY_CONDITION_OPERATORS.LT]: tKey(
    'pricing_assistant.recommendations.price_range.formatted_condition.lt',
  ),
  [PRICE_RANGE_STRATEGY_CONDITION_OPERATORS.GTE]: tKey(
    'pricing_assistant.recommendations.price_range.formatted_condition.gte',
  ),
  [PRICE_RANGE_STRATEGY_CONDITION_OPERATORS.LTE]: tKey(
    'pricing_assistant.recommendations.price_range.formatted_condition.lte',
  ),
};

export const COMPETITORS_IMPACTS_STRATEGY_LEVELS = {
  MUCH_MORE_EXPENSIVE: 'much_more_expensive',
  MORE_EXPENSIVE: 'more_expensive',
  SIMILAR: 'similar',
  CHEAPER: 'cheaper',
  MUCH_CHEAPER: 'much_cheaper',
  IRRELEVANT: 'irrelevant',
} as const;

export const COMPETITOR_IMPACT_STRATEGY_INIT_EMPTY = 'emptyByFE' as const;

export const COMPETITORS_IMPACTS_STRATEGY_LEVELS_TRANSLATION_KEY_MAPPING = {
  [COMPETITORS_IMPACTS_STRATEGY_LEVELS.MUCH_MORE_EXPENSIVE]: tKey(
    'pricing_assistant.recommendations.competitors_impacts.levels.much_more_expensive',
  ),
  [COMPETITORS_IMPACTS_STRATEGY_LEVELS.MORE_EXPENSIVE]: tKey(
    'pricing_assistant.recommendations.competitors_impacts.levels.more_expensive',
  ),
  [COMPETITORS_IMPACTS_STRATEGY_LEVELS.SIMILAR]: tKey(
    'pricing_assistant.recommendations.competitors_impacts.levels.similar',
  ),
  [COMPETITORS_IMPACTS_STRATEGY_LEVELS.CHEAPER]: tKey(
    'pricing_assistant.recommendations.competitors_impacts.levels.cheaper',
  ),
  [COMPETITORS_IMPACTS_STRATEGY_LEVELS.MUCH_CHEAPER]: tKey(
    'pricing_assistant.recommendations.competitors_impacts.levels.much_cheaper',
  ),
  [COMPETITORS_IMPACTS_STRATEGY_LEVELS.IRRELEVANT]: tKey(
    'pricing_assistant.recommendations.competitors_impacts.levels.irrelevant',
  ),
} as const;

export const COMPETITORS_IMPACTS_STRATEGY_LEVELS_ICON_CLASSES = {
  [COMPETITORS_IMPACTS_STRATEGY_LEVELS.MUCH_MORE_EXPENSIVE]:
    'data-table__icon--much-more-expensive',
  [COMPETITORS_IMPACTS_STRATEGY_LEVELS.MORE_EXPENSIVE]:
    'data-table__icon--more-expensive',
  [COMPETITORS_IMPACTS_STRATEGY_LEVELS.SIMILAR]: 'data-table__icon--similar',
  [COMPETITORS_IMPACTS_STRATEGY_LEVELS.CHEAPER]: 'data-table__icon--cheaper',
  [COMPETITORS_IMPACTS_STRATEGY_LEVELS.MUCH_CHEAPER]:
    'data-table__icon--much-cheaper',
  [COMPETITORS_IMPACTS_STRATEGY_LEVELS.IRRELEVANT]:
    'data-table__icon--irrelevant',
} as const;

export const COMPETITORS_IMPACTS_STRATEGY_DAY_OF_WEEK_ADJUSTMENT_OPTIONS = {
  ANY_DAY: 'any_day',
  PER_DAY: 'per_day',
};

// Open Pricing
// Open pricing room categories include CeMa categories and "Unmapped" category
export const OPEN_ROOM_PRICING_ROOM_CATEGORIES = {
  ...CENTRAL_MAPPING_FIXED_CATEGORIES,
  UNMAPPED: 'Unmapped',
} as const;
export type OpenRoomPricingFixedCategory =
  (typeof OPEN_ROOM_PRICING_ROOM_CATEGORIES)[keyof typeof OPEN_ROOM_PRICING_ROOM_CATEGORIES];

export const OPEN_ROOM_PRICING_ROOM_CATEGORIES_TRANSLATIONS = {
  [OPEN_ROOM_PRICING_ROOM_CATEGORIES.STANDARD_ROOM]: tKey(
    'rates.roomtypes.standard',
  ),
  [OPEN_ROOM_PRICING_ROOM_CATEGORIES.PREMIUM_ROOM]: tKey(
    'rates.roomtypes.premium',
  ),
  [OPEN_ROOM_PRICING_ROOM_CATEGORIES.BUDGET_ROOM]: tKey(
    'rates.roomtype.budget',
  ),
  [OPEN_ROOM_PRICING_ROOM_CATEGORIES.SINGLE_ROOM]: tKey(
    'rates.roomtypes.single',
  ),
  [OPEN_ROOM_PRICING_ROOM_CATEGORIES.FAMILY_ROOM]: tKey(
    'rates.roomtypes.family',
  ),
  [OPEN_ROOM_PRICING_ROOM_CATEGORIES.SUITE]: tKey('rates.roomtypes.suite'),
  [OPEN_ROOM_PRICING_ROOM_CATEGORIES.APARTMENT]: tKey(
    'rates.roomtypes.apartment',
  ),
  [OPEN_ROOM_PRICING_ROOM_CATEGORIES.VILLA]: tKey('rates.roomtypes.villa'),
  [OPEN_ROOM_PRICING_ROOM_CATEGORIES.OTHER]: tKey('general.other'),
  [OPEN_ROOM_PRICING_ROOM_CATEGORIES.UNMAPPED]: tKey('general.unmapped'),
} as const;

// CeMa room category to Rate Insight room type mapping (https://docs.google.com/spreadsheets/d/1L0EwKHnwsE2NUZiBxxhusGI994Hn9TZAbO2WJl8zzXM/edit?gid=412235917#gid=412235917)
export const OPEN_PRICING_ROOM_CATEGORY_TO_RI_ROOMTYPE_MAPPING = {
  [OPEN_ROOM_PRICING_ROOM_CATEGORIES.STANDARD_ROOM]: ROOMTYPES.STANDARD,
  [OPEN_ROOM_PRICING_ROOM_CATEGORIES.PREMIUM_ROOM]: ROOMTYPES.PREMIUM,
  [OPEN_ROOM_PRICING_ROOM_CATEGORIES.BUDGET_ROOM]: ROOMTYPES.BUDGET,
  [OPEN_ROOM_PRICING_ROOM_CATEGORIES.SINGLE_ROOM]: ROOMTYPES.SINGLE,
  [OPEN_ROOM_PRICING_ROOM_CATEGORIES.FAMILY_ROOM]: ROOMTYPES.FAMILY,
  [OPEN_ROOM_PRICING_ROOM_CATEGORIES.SUITE]: ROOMTYPES.SUITE,
  [OPEN_ROOM_PRICING_ROOM_CATEGORIES.APARTMENT]: ROOMTYPES.APARTMENT,
  [OPEN_ROOM_PRICING_ROOM_CATEGORIES.VILLA]: ROOMTYPES.VILLA,
};
export type OpenPricingRoomCategoryToRIRoomTypes =
  keyof typeof OPEN_PRICING_ROOM_CATEGORY_TO_RI_ROOMTYPE_MAPPING;

export const RI_ROOMTYPE_TO_OPEN_PRICING_ROOM_CATEGORY_MAPPING =
  Object.fromEntries(
    Object.entries(OPEN_PRICING_ROOM_CATEGORY_TO_RI_ROOMTYPE_MAPPING).map(
      ([category, roomType]) => [roomType, category],
    ),
  );

// CeMa is updating their sorting order to match with RI. However, for them this sorting order is provided by BE.
// But for PriMa, we define these options in the FE and and now need a sorting order for the same.
export const OPEN_PRICING_ROOM_CATEGORY_SORT_ORDER = [
  OPEN_ROOM_PRICING_ROOM_CATEGORIES.BUDGET_ROOM,
  OPEN_ROOM_PRICING_ROOM_CATEGORIES.STANDARD_ROOM,
  OPEN_ROOM_PRICING_ROOM_CATEGORIES.SINGLE_ROOM,
  OPEN_ROOM_PRICING_ROOM_CATEGORIES.PREMIUM_ROOM,
  OPEN_ROOM_PRICING_ROOM_CATEGORIES.SUITE,
  OPEN_ROOM_PRICING_ROOM_CATEGORIES.FAMILY_ROOM,
  OPEN_ROOM_PRICING_ROOM_CATEGORIES.APARTMENT,
  OPEN_ROOM_PRICING_ROOM_CATEGORIES.VILLA,
  OPEN_ROOM_PRICING_ROOM_CATEGORIES.OTHER,
  OPEN_ROOM_PRICING_ROOM_CATEGORIES.UNMAPPED,
];

export const PRIMA_VIEWS = {
  CALENDAR: 'calendar',
  TABLE: 'table',
};

export const BULK_UPDATE_OPTIONS = {
  ACCEPT_RECOMMENDATIONS: 'accept_recommendations',
  EDIT_PRICES: 'edit_prices',
} as const;

export type BulkUpdateRadioOption =
  (typeof BULK_UPDATE_OPTIONS)[keyof typeof BULK_UPDATE_OPTIONS];

export const BULK_UPDATE_MAX_FUTURE_YEARS = 1;

export const BULK_UPDATE_PRICE_OPTIONS = {
  CHANGE_BY: 'change_by',
  EXACT: 'exact',
} as const;

export type BulkUpdatePriceOption =
  (typeof BULK_UPDATE_PRICE_OPTIONS)[keyof typeof BULK_UPDATE_PRICE_OPTIONS];

export const RECOMMENDATIONS_ACTION_ENDPOINT_ERROR_CODES = {
  DUPLICATE_RECOMMENDATION_ACTION: 'duplicate_recommendation_action',
  NEGATIVE_RATE_VALUE: 'negative_rate_value',
};

export const RECOMMENDATION_SUMMARISED_REASONS = {
  POSITION_COMPSET_FORECAST: '1',
  LOW_OCCUPANCY_RAISE_PRICE: '2',
  INCREASE_WITH_COMPETITION: '3',
  KEEP_PRICE_LOW_OCCUPANCY: '4',
  NO_DISCOUNT_NEEDED: '5',
  INCREASE_WITH_COMPETITION_MORE_ROOMS: '6',
  BUSY_DAY_EXPECTED: '7',
  POSITION_IN_COMPETITION: '8',
  HIGH_PICKUP_INCREASE: '9',
  BUSY_DAY_INCREASE: '10',
  HIGH_PICKUP_LOW_OCCUPANCY: '11',
  LOW_OCCUPANCY_MATCH_COMPETITION: '12',
  LOW_PICKUP_AND_OCCUPANCY: '13',
  HIGH_PICKUP_RAISE_PRICE: '14',
  INCREASE_WITH_COMPETITION_NO_BLOCK: '15',
  KEEP_PRICE_VS_COMPETITION: '16',
  NO_HIGHER_PRICE_NEEDED: '17',
  INCREASE_DESPITE_LOW_PICKUP: '18',
  LOW_PICKUP_REDUCE: '19',
  HIGH_PICKUP_RAISE: '20',
  LOWER_PRICE_CURRENT_OCCUPANCY: '21',
  FOLLOW_COMPETITION_INCREASE: '1N',
  OPPORTUNITY_TO_INCREASE: '2N',
  POSITION_BETTER_COMPETITION: '3N',
  DECREASE_STAY_COMPETITIVE: '4N',
  USER_SET_MINIMUM: '1UR',
  USER_SET_MAXIMUM: '2UR',
} as const;

export type RecommendationSummarisedReasons =
  (typeof RECOMMENDATION_SUMMARISED_REASONS)[keyof typeof RECOMMENDATION_SUMMARISED_REASONS];

export const RECOMMENDATION_SUMMARISED_REASONS_TRANSLATION_KEY_MAPPING: Record<
  RecommendationSummarisedReasons,
  string
> = {
  [RECOMMENDATION_SUMMARISED_REASONS.POSITION_COMPSET_FORECAST]: tKey(
    'pricing_assistant.reason.summarised.1',
  ),
  [RECOMMENDATION_SUMMARISED_REASONS.LOW_OCCUPANCY_RAISE_PRICE]: tKey(
    'pricing_assistant.reason.summarised.2',
  ),
  [RECOMMENDATION_SUMMARISED_REASONS.INCREASE_WITH_COMPETITION]: tKey(
    'pricing_assistant.reason.summarised.3',
  ),
  [RECOMMENDATION_SUMMARISED_REASONS.KEEP_PRICE_LOW_OCCUPANCY]: tKey(
    'pricing_assistant.reason.summarised.4',
  ),
  [RECOMMENDATION_SUMMARISED_REASONS.NO_DISCOUNT_NEEDED]: tKey(
    'pricing_assistant.reason.summarised.5',
  ),
  [RECOMMENDATION_SUMMARISED_REASONS.INCREASE_WITH_COMPETITION_MORE_ROOMS]:
    tKey('pricing_assistant.reason.summarised.6'),
  [RECOMMENDATION_SUMMARISED_REASONS.BUSY_DAY_EXPECTED]: tKey(
    'pricing_assistant.reason.summarised.7',
  ),
  [RECOMMENDATION_SUMMARISED_REASONS.POSITION_IN_COMPETITION]: tKey(
    'pricing_assistant.reason.summarised.8',
  ),
  [RECOMMENDATION_SUMMARISED_REASONS.HIGH_PICKUP_INCREASE]: tKey(
    'pricing_assistant.reason.summarised.9',
  ),
  [RECOMMENDATION_SUMMARISED_REASONS.BUSY_DAY_INCREASE]: tKey(
    'pricing_assistant.reason.summarised.10',
  ),
  [RECOMMENDATION_SUMMARISED_REASONS.HIGH_PICKUP_LOW_OCCUPANCY]: tKey(
    'pricing_assistant.reason.summarised.11',
  ),
  [RECOMMENDATION_SUMMARISED_REASONS.LOW_OCCUPANCY_MATCH_COMPETITION]: tKey(
    'pricing_assistant.reason.summarised.12',
  ),
  [RECOMMENDATION_SUMMARISED_REASONS.LOW_PICKUP_AND_OCCUPANCY]: tKey(
    'pricing_assistant.reason.summarised.13',
  ),
  [RECOMMENDATION_SUMMARISED_REASONS.HIGH_PICKUP_RAISE_PRICE]: tKey(
    'pricing_assistant.reason.summarised.14',
  ),
  [RECOMMENDATION_SUMMARISED_REASONS.INCREASE_WITH_COMPETITION_NO_BLOCK]: tKey(
    'pricing_assistant.reason.summarised.15',
  ),
  [RECOMMENDATION_SUMMARISED_REASONS.KEEP_PRICE_VS_COMPETITION]: tKey(
    'pricing_assistant.reason.summarised.16',
  ),
  [RECOMMENDATION_SUMMARISED_REASONS.NO_HIGHER_PRICE_NEEDED]: tKey(
    'pricing_assistant.reason.summarised.17',
  ),
  [RECOMMENDATION_SUMMARISED_REASONS.INCREASE_DESPITE_LOW_PICKUP]: tKey(
    'pricing_assistant.reason.summarised.18',
  ),
  [RECOMMENDATION_SUMMARISED_REASONS.LOW_PICKUP_REDUCE]: tKey(
    'pricing_assistant.reason.summarised.19',
  ),
  [RECOMMENDATION_SUMMARISED_REASONS.HIGH_PICKUP_RAISE]: tKey(
    'pricing_assistant.reason.summarised.20',
  ),
  [RECOMMENDATION_SUMMARISED_REASONS.LOWER_PRICE_CURRENT_OCCUPANCY]: tKey(
    'pricing_assistant.reason.summarised.21',
  ),
  [RECOMMENDATION_SUMMARISED_REASONS.FOLLOW_COMPETITION_INCREASE]: tKey(
    'pricing_assistant.reason.summarised.1N',
  ),
  [RECOMMENDATION_SUMMARISED_REASONS.OPPORTUNITY_TO_INCREASE]: tKey(
    'pricing_assistant.reason.summarised.2N',
  ),
  [RECOMMENDATION_SUMMARISED_REASONS.POSITION_BETTER_COMPETITION]: tKey(
    'pricing_assistant.reason.summarised.3N',
  ),
  [RECOMMENDATION_SUMMARISED_REASONS.DECREASE_STAY_COMPETITIVE]: tKey(
    'pricing_assistant.reason.summarised.4N',
  ),
  [RECOMMENDATION_SUMMARISED_REASONS.USER_SET_MINIMUM]: tKey(
    'pricing_assistant.reason.summarised.1UR',
  ),
  [RECOMMENDATION_SUMMARISED_REASONS.USER_SET_MAXIMUM]: tKey(
    'pricing_assistant.reason.summarised.2UR',
  ),
} as const;

// These limits are used to determine if the new price is within the suggested range, so a warning can be shown if not
export const NEW_PRICE_LOWER_LIMIT_FACTOR = 0.5;
export const NEW_PRICE_UPPER_LIMIT_FACTOR = 1.5;

/*
  Possible override types
  The values should correspond to override types expected by the BE API
*/
export const OVERRIDE_TYPES = {
  EXACT_PRICE: 'exact_price',
  PRICE_RANGE: 'price_range',
  DISCOUNT: 'exact_discount',
  OVERBOOKING: 'OVERBOOKING',
  MIN_LOS: 'MIN_LOS',
} as const;

export type OverrideTypes =
  (typeof OVERRIDE_TYPES)[keyof typeof OVERRIDE_TYPES];

export type OverrideVariant = 'bullet' | 'automation-off';

export type PricingOverrideTypes = (typeof OVERRIDE_TYPES)[
  | 'EXACT_PRICE'
  | 'PRICE_RANGE'
  | 'DISCOUNT'];

export const OVERRIDE_TYPES_USAGE_TRACKING = {
  [OVERRIDE_TYPES.EXACT_PRICE]: 'exact_price',
  [OVERRIDE_TYPES.PRICE_RANGE]: 'price_range',
  [OVERRIDE_TYPES.DISCOUNT]: 'discount',
} as const;

export type PricePreviewRooms = {
  currentPrice?: number;
  formattedCurrentPrice?: string | null;
  overrideType: OverrideTypes | null;
  pmsRoomType: string;
  roomName: string;
  showOverride: boolean;
}[];

export const RESTRICTION_TYPES = {
  OVERBOOKING: 'OVERBOOKING',
  MIN_LOS: 'MIN_LOS',
} as const;

export const RESTRICTION_MODEL_CONTEXTS = {
  HOTEL_LEVEL: 'HOTEL_LEVEL',
  RATE_GROUP_LEVEL: 'RATE_GROUP_LEVEL',
} as const;

export type RestrictionModelContexts =
  (typeof RESTRICTION_MODEL_CONTEXTS)[keyof typeof RESTRICTION_MODEL_CONTEXTS];

export const DAY_OVERRIDE_MODAL_TAB_IDS = {
  OVERBOOKING: 'OVERBOOKING',
  STAY_RESTRICTIONS: 'STAY_RESTRICTIONS',
} as const;

export type DayOverrideModalTabIds = keyof typeof DAY_OVERRIDE_MODAL_TAB_IDS;

export const MIN_LOS_RESTRICTION_TYPE = {
  MIN_LOS: 'min_los',
  NO_RESTRICTION: 'no_restriction',
} as const;

export type MinLosRestrictionType =
  (typeof MIN_LOS_RESTRICTION_TYPE)[keyof typeof MIN_LOS_RESTRICTION_TYPE];

// Default of 2 ensures no validation error on first send (per spec)
export const MIN_LOS_DEFAULT_VALUE = 2;
/* MinLOS value 0 represents "no minimum stay restriction":
   - Incoming (recommendation): BE returns 0 when it recommends removing the minLOS restriction.
   - Outgoing (override): when the user selects "No MinLOS" and sends, we push an override
     with value 0 to instruct the PMS to remove the restriction. */
export const MIN_LOS_NO_RESTRICTION_VALUE = 0;
